import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Siren, Send, Loader2, RefreshCw, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const Toggle = ({ on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="relative w-9 h-5 rounded-full transition-colors shrink-0"
    style={{ background: on ? "var(--ws-blue,#38bdf8)" : "rgba(120,140,180,0.3)" }}>
    <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: on ? 18 : 2 }} />
  </button>
);

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-[12px] text-[color:var(--ws-text)]">{label}</span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const Pills = ({ options, value, onChange, testid }) => (
  <div className="flex items-center gap-1" data-testid={testid}>
    {options.map((o) => (
      <button key={o.v} onClick={() => onChange(o.v)}
        className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${String(value) === String(o.v) ? "ws-btn-primary" : "ws-btn-ghost"}`}>
        {o.l}
      </button>
    ))}
  </div>
);

const STATUS = {
  scheduled: { c: "#64748b", t: "Scheduled" },
  upcoming: { c: "#f59e0b", t: "Upcoming" },
  imminent: { c: "#ef4444", t: "Imminent" },
  done: { c: "#22c55e", t: "Done" },
};

const NewsAlertsAdmin = () => {
  const [s, setS] = useState(null);
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const [rs, re] = await Promise.all([
        fetch(`${API}/news/alert-settings`, { credentials: "include" }),
        fetch(`${API}/news/alert-events?limit=40`, { credentials: "include" }),
      ]);
      if (rs.ok) setS(await rs.json());
      if (re.ok) setEvents((await re.json()).events);
    } catch { /* hidden for non-admins */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setS((p) => ({ ...p, ...patch }));
    try {
      const r = await fetch(`${API}/news/alert-settings`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch),
      });
      if (r.ok) { setS(await r.json()); }
    } catch { toast.error("Could not save"); }
  };
  const saveCat = (key) => save({ categories: { ...(s.categories || {}), [key]: !s.categories?.[key] } });

  const test = async () => {
    setBusy("test");
    try {
      const r = await fetch(`${API}/news/alert-test`, { method: "POST", credentials: "include" });
      const d = await r.json();
      toast[d.telegram_ok ? "success" : "error"](d.telegram_ok ? "Test alert sent to Telegram ✅" : "Telegram send failed");
    } catch { toast.error("Test failed"); } finally { setBusy(""); }
  };

  const fmt = (ts) => new Intl.DateTimeFormat(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: TZ,
  }).format(new Date(ts * 1000));

  if (!s) return null;

  return (
    <div className="ws-panel p-4 mb-4 ws-fade-up" data-testid="news-alerts-admin">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="ws-icon-chip w-8 h-8"><Siren className="w-4 h-4 text-[#f59e0b]" /></div>
          <div>
            <div className="ws-heading text-[13px] font-bold text-white">High-Volatility News Alerts</div>
            <div className="text-[11px] text-[color:var(--ws-muted)]">Calendar → Journal + Telegram · times in {TZ}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={test} disabled={!!busy} className="ws-btn-primary px-2.5 py-1.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1.5" data-testid="alert-test-btn">
            {busy === "test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Test
          </button>
          <button onClick={load} className="ws-btn-ghost w-8 h-8 rounded-lg flex items-center justify-center" data-testid="alert-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
          <button onClick={() => setOpen((o) => !o)} className="ws-btn-ghost px-2 py-1.5 rounded-lg text-[11px] font-semibold">{open ? "Hide" : "Settings"}</button>
        </div>
      </div>

      {open && (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 mt-3 pt-3 border-t border-[color:var(--ws-border)]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-blue-bright)] mb-1">Alert types</div>
            <Row label="🟠 Upcoming alert"><Toggle on={s.enabled_upcoming} onClick={() => save({ enabled_upcoming: !s.enabled_upcoming })} testid="toggle-upcoming" /></Row>
            <Row label="🔴 Pre-news alert"><Toggle on={s.enabled_pre} onClick={() => save({ enabled_pre: !s.enabled_pre })} testid="toggle-pre" /></Row>
            <Row label="🔴 Post-news result"><Toggle on={s.enabled_result} onClick={() => save({ enabled_result: !s.enabled_result })} testid="toggle-result" /></Row>
            <Row label="Telegram publishing"><Toggle on={s.telegram_enabled} onClick={() => save({ telegram_enabled: !s.telegram_enabled })} testid="toggle-telegram" /></Row>
            <Row label="Journal publishing"><Toggle on={s.journal_enabled} onClick={() => save({ journal_enabled: !s.journal_enabled })} testid="toggle-journal" /></Row>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-blue-bright)] mb-1">Timing & importance</div>
            <Row label="Upcoming lead"><Pills testid="pills-upcoming" value={s.upcoming_hours} onChange={(v) => save({ upcoming_hours: v })} options={[{ v: 1, l: "1h" }, { v: 3, l: "3h" }, { v: 6, l: "6h" }, { v: 12, l: "12h" }]} /></Row>
            <Row label="Pre-news"><Pills testid="pills-pre" value={s.pre_minutes} onChange={(v) => save({ pre_minutes: v })} options={[{ v: 5, l: "5m" }, { v: 10, l: "10m" }, { v: 15, l: "15m" }, { v: 30, l: "30m" }]} /></Row>
            <Row label="Result delay"><Pills testid="pills-delay" value={s.result_delay_minutes} onChange={(v) => save({ result_delay_minutes: v })} options={[{ v: 1, l: "1m" }, { v: 3, l: "3m" }, { v: 5, l: "5m" }, { v: 15, l: "15m" }]} /></Row>
            <Row label="Min importance"><Pills testid="pills-importance" value={s.importance} onChange={(v) => save({ importance: v })} options={[{ v: "LOW", l: "Low" }, { v: "MEDIUM", l: "Med" }, { v: "HIGH", l: "High" }]} /></Row>
            <div className="mt-1">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-blue-bright)] mb-1">Categories</div>
              <div className="flex flex-wrap gap-1.5" data-testid="alert-categories">
                {["forex", "gold", "crypto", "indices", "central_banks", "macro"].map((k) => (
                  <button key={k} onClick={() => saveCat(k)}
                    className={`px-2 py-1 rounded-md text-[11px] font-semibold capitalize transition-colors ${s.categories?.[k] ? "ws-btn-primary" : "ws-btn-ghost"}`}>
                    {k.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-[color:var(--ws-border)]">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-blue-bright)] mb-2">
          <CalendarClock className="w-3.5 h-3.5" /> Tracked high-volatility events
        </div>
        <div className="space-y-1.5 max-h-[280px] overflow-auto pr-1" data-testid="alert-events-list">
          {(events || []).length === 0 && <div className="text-[12px] text-[color:var(--ws-muted)] py-2">No matching events this week.</div>}
          {(events || []).map((e) => {
            const st = STATUS[e.status] || STATUS.scheduled;
            return (
              <div key={e.id} className="flex items-center gap-3 px-2.5 py-2 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }} data-testid="alert-event-row">
                <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: st.c }} />
                <span className="text-[15px]">{e.flag}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold text-white truncate">{e.currency} {e.title}</div>
                  <div className="text-[11px] text-[color:var(--ws-muted)]">{fmt(e.event_ts)} · F {e.forecast} · P {e.previous} · A {e.actual}</div>
                </div>
                <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: st.c, background: `${st.c}22` }}>{st.t}</span>
                <div className="flex gap-0.5 shrink-0">
                  <span title="Upcoming sent" className="w-2 h-2 rounded-full" style={{ background: e.sent.upcoming ? "#f59e0b" : "rgba(255,255,255,0.12)" }} />
                  <span title="Pre sent" className="w-2 h-2 rounded-full" style={{ background: e.sent.pre ? "#ef4444" : "rgba(255,255,255,0.12)" }} />
                  <span title="Result sent" className="w-2 h-2 rounded-full" style={{ background: e.sent.result ? "#22c55e" : "rgba(255,255,255,0.12)" }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NewsAlertsAdmin;
